/**
 * Savings / Digital FTE gained.
 *
 * Hero = Digital FTEs gained per year (annualised week run-rate / 1,540h).
 * Below: £ + hours KPIs, daily trend, agent leaderboard, editable rules table.
 *
 * Rules are in `savings_rules` DB table, editable inline. Multipliers
 * immediately invalidate the summary cache when saved.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  BanknoteIcon, Users, Zap, Clock, RefreshCw, Save, CheckCircle2, XCircle, TrendingUp,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface BreakdownRow {
  category: string
  label?: string
  count: number
  minutes_each: number
  hours: number
  gbp: number
}

interface Summary {
  today:    { hours: number; gbp: number; breakdown: BreakdownRow[] }
  week:     { hours: number; gbp: number }
  month:    { hours: number; gbp: number }
  ytd:      { hours: number; gbp: number }
  lifetime: { hours: number; gbp: number; first_seen: string | null; breakdown: BreakdownRow[] }
  annual_rate_hours: number
  annual_rate_gbp:   number
  fte_equivalent:    number
  fte_hours_per_year: number
  fte_definition:    string | null
  hourly_rate_gbp:   number
  daily_rate_gbp:    number
  daily_trend: { date: string; hours: number; gbp: number }[]
  generated_at: string
}

interface Rule {
  category: string
  label: string
  description: string
  minutes_each: number
  enabled: boolean
}

interface Setting {
  key: string
  value_number: number
  description: string
}

const fmtGBP = (n: number): string =>
  '£' + Math.round(n).toLocaleString('en-GB')

const fmtHours = (n: number): string =>
  n >= 1000 ? (n / 1000).toFixed(1) + 'k h' : n.toFixed(n >= 100 ? 0 : 1) + ' h'

export default function SavingsPage() {
  const [summary, setSummary] = useState<Summary | null>(null)
  const [rules, setRules] = useState<Rule[]>([])
  const [settings, setSettings] = useState<Setting[]>([])
  const [loading, setLoading] = useState(false)
  const [savingRule, setSavingRule] = useState<string | null>(null)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [dirty, setDirty] = useState<Record<string, Partial<Rule>>>({})
  const [dirtySettings, setDirtySettings] = useState<Record<string, number>>({})

  const refresh = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/savings/summary').then(r => setSummary(r.data)).catch(() => {}),
      api.get('/api/savings/rules').then(r => setRules(r.data?.rules || [])).catch(() => {}),
      api.get('/api/savings/settings').then(r => setSettings(r.data?.settings || [])).catch(() => {}),
    ]).finally(() => setLoading(false))
  }

  const saveSetting = async (key: string) => {
    const v = dirtySettings[key]
    if (v === undefined) return
    setSavingKey(key)
    try {
      await api.put(`/api/savings/settings/${encodeURIComponent(key)}`, { value_number: v })
      setDirtySettings(d => { const { [key]: _, ...rest } = d; return rest })
      refresh()
    } finally {
      setSavingKey(null)
    }
  }
  useEffect(() => { refresh() }, [])

  const saveRule = async (category: string) => {
    const base = rules.find(r => r.category === category)
    if (!base) return
    const next = { ...base, ...dirty[category] }
    setSavingRule(category)
    try {
      await api.put(`/api/savings/rules/${encodeURIComponent(category)}`, {
        label:        next.label,
        description:  next.description,
        minutes_each: Number(next.minutes_each) || 0,
        enabled:      !!next.enabled,
      })
      setDirty(d => { const { [category]: _, ...rest } = d; return rest })
      refresh()
    } finally {
      setSavingRule(null)
    }
  }

  const patch = (category: string, field: keyof Rule, value: any) =>
    setDirty(d => ({ ...d, [category]: { ...d[category], [field]: value } }))

  const rulesForLookup: Record<string, Rule> = useMemo(
    () => Object.fromEntries(rules.map(r => [r.category, r])),
    [rules],
  )

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border">
        <h1 className="text-lg font-bold text-emerald flex items-center gap-2">
          <Users size={18} /> HEADCOUNT AVOIDED
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          Analyst hours the platform replaces · agent leaderboard
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* HERO — flips between two narratives:
          • below 1 FTE: lead with HOURS / year (the FTE fraction reads
            small and unflattering, so we show it as a sub-stat instead);
          • at or above 1 FTE: lead with the FTE count (e.g. "1.4 Digital
            FTEs avoided") because that's the moment the number becomes
            board-room interesting. */}
      {(() => {
        if (!summary) {
          return (
            <section className="card p-6 bg-gradient-to-br from-emerald/10 via-surface-card to-surface-card border-emerald/30">
              <div className="text-text-muted text-sm">Loading…</div>
            </section>
          )
        }
        const fte = summary.fte_equivalent
        const hours = Math.round(summary.annual_rate_hours)
        const showFteHero = fte >= 1
        const fteProgress = Math.max(0, Math.min(1, fte))
        return (
          <section className="card p-6 bg-gradient-to-br from-emerald/10 via-surface-card to-surface-card border-emerald/30">
            {showFteHero ? (
              <>
                <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-light font-semibold flex items-center gap-2">
                  <Users size={13} /> Digital FTEs avoided · 12-month projection (trailing 30d × 12)
                </div>
                <div className="mt-3 flex items-end flex-wrap gap-x-8 gap-y-3">
                  <div className="flex items-baseline gap-3">
                    <span className="text-[96px] leading-none font-black font-mono text-emerald tabular-nums">
                      {fte.toFixed(2)}
                    </span>
                    <span className="text-2xl font-semibold text-emerald/70 tracking-widest">FTE</span>
                  </div>
                  <div className="pb-2 text-sm text-text-muted">
                    <span className="text-emerald font-semibold">{hours.toLocaleString()}</span>{' '}
                    hours / year of analyst work the platform avoids needing
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-3 max-w-3xl leading-relaxed">
                  {summary.fte_definition || `FTE = ${summary.fte_hours_per_year} hours / year`}. Not a money-saved
                  claim — this is headcount the platform lets you avoid hiring. If you had to do this work by hand,
                  you'd need <span className="text-emerald font-semibold">{fte.toFixed(2)} extra analysts</span> on staff.
                </p>
              </>
            ) : (
              <>
                <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-light font-semibold flex items-center gap-2">
                  <Users size={13} /> Analyst hours avoided · 12-month projection (trailing 30d × 12)
                </div>
                <div className="mt-3 flex items-end flex-wrap gap-x-8 gap-y-3">
                  <div className="flex items-baseline gap-3">
                    <span className="text-[96px] leading-none font-black font-mono text-emerald tabular-nums">
                      {hours.toLocaleString()}
                    </span>
                    <span className="text-2xl font-semibold text-emerald/70 tracking-widest">h / year</span>
                  </div>
                </div>
                {/* Progress toward 1 whole FTE — the metric only graduates to
                    the FTE narrative once the bar fills. Tells a "we're
                    building toward measurable headcount avoidance" story
                    without hyping a sub-1 fraction. */}
                <div className="mt-4 max-w-xl">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-text-muted">
                    <span>Progress toward 1 FTE-year</span>
                    <span className="font-mono text-emerald-light">{(fteProgress * 100).toFixed(0)}%</span>
                  </div>
                  <div className="h-1.5 mt-1 rounded bg-surface-raised overflow-hidden">
                    <div className="h-full bg-emerald" style={{ width: `${fteProgress * 100}%` }} />
                  </div>
                  <div className="text-[10px] text-text-muted mt-1 font-mono">
                    1 FTE = {summary.fte_hours_per_year.toLocaleString()} h ({fte.toFixed(2)} so far)
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-4 max-w-3xl leading-relaxed">
                  Conservative bottom-up attribution: every automated event the platform handles is credited at the
                  marginal analyst time it would have taken to do by hand (rules editable below). Once this passes
                  one whole FTE, the headline flips to a "Digital FTEs avoided" reading.
                </p>
              </>
            )}
          </section>
        )
      })()}

      {/* KPI row — hours, with a 30-day sparkline below each so trends are
          visible at a glance and an operator can spot a stalled ingest pipeline
          without having to scroll. */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiTile label="Today"      hours={summary.today.hours}   trend={summary.daily_trend} tone="blue"    windowDays={1} />
          <KpiTile label="This week"  hours={summary.week.hours}    trend={summary.daily_trend} tone="emerald" windowDays={7} />
          <KpiTile label="This month" hours={summary.month.hours}   trend={summary.daily_trend} tone="amber"   windowDays={30} />
          <KpiTile label="YTD"        hours={summary.ytd.hours}     trend={summary.daily_trend} tone="rose"    windowDays={30} />
        </div>
      )}

      {/* Daily trend */}
      {summary && summary.daily_trend.length > 0 && (
        <section className="card p-5">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-text-primary mb-3 flex items-center gap-2">
            <TrendingUp size={14} className="text-emerald" /> 30-day trend — hours avoided per day
          </h2>
          <DailyTrendChart data={summary.daily_trend} />
        </section>
      )}

      {/* Agent leaderboard */}
      {summary && (
        <section className="card p-0">
          <header className="p-3 border-b border-border flex items-center gap-2 flex-wrap">
            <Zap size={14} className="text-emerald" />
            <h2 className="text-sm font-semibold uppercase tracking-widest">Agent leaderboard · lifetime</h2>
            <span className="text-[10px] text-text-muted">which automations have saved the most</span>
            <span className="ml-auto text-[10px] text-text-muted">
              {summary.lifetime.first_seen
                ? `since ${summary.lifetime.first_seen.slice(0, 10)}`
                : ''}
            </span>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-surface-raised text-text-muted">
                <tr>
                  <th className="text-left  px-3 py-2">#</th>
                  <th className="text-left  px-3 py-2">Agent</th>
                  <th className="text-right px-3 py-2">Events</th>
                  <th className="text-right px-3 py-2">Min each</th>
                  <th className="text-right px-3 py-2">Hours saved</th>
                  <th className="text-right px-3 py-2">£ saved</th>
                  <th className="text-left  px-3 py-2 w-1/3">Share</th>
                </tr>
              </thead>
              <tbody>
                {summary.lifetime.breakdown.map((b, i) => {
                  const rule = rulesForLookup[b.category]
                  const pct  = summary.lifetime.gbp ? (b.gbp / summary.lifetime.gbp) * 100 : 0
                  return (
                    <tr key={b.category} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-text-muted">{i + 1}</td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-text-primary">{rule?.label || b.category}</div>
                        {rule?.description && <div className="text-[10px] text-text-muted">{rule.description}</div>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{b.count.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono text-text-muted">{b.minutes_each}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtHours(b.hours)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-light">{fmtGBP(b.gbp)}</td>
                      <td className="px-3 py-2">
                        <div className="h-2 rounded bg-surface-raised overflow-hidden">
                          <div className="h-full bg-emerald/60" style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <div className="text-[9px] text-text-muted font-mono mt-0.5">{pct.toFixed(1)}%</div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Editable settings (rates + FTE) */}
      <section className="card p-0">
        <header className="p-3 border-b border-border flex items-center gap-2">
          <BanknoteIcon size={14} className="text-emerald" />
          <h2 className="text-sm font-semibold uppercase tracking-widest">Settings · analyst rate + FTE baseline</h2>
          <span className="text-[10px] text-text-muted ml-1">changes apply instantly (cache invalidated on save)</span>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised text-text-muted">
              <tr>
                <th className="text-left  px-3 py-2 w-1/4">Key</th>
                <th className="text-left  px-3 py-2">Description</th>
                <th className="text-right px-3 py-2 w-32">Value</th>
                <th className="text-right px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {settings.map(s => {
                const draft = dirtySettings[s.key]
                const isDirty = draft !== undefined
                const current = isDirty ? draft : s.value_number
                return (
                  <tr key={s.key} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-text-muted">{s.key}</td>
                    <td className="px-3 py-2 text-text-secondary">{s.description}</td>
                    <td className="px-3 py-2 text-right">
                      <input className="input text-right w-28 font-mono" type="number" step="1"
                        aria-label={`Value for ${s.key}`}
                        value={current}
                        onChange={e => setDirtySettings(d => ({ ...d, [s.key]: parseFloat(e.target.value) || 0 }))} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isDirty ? (
                        <button type="button" onClick={() => saveSetting(s.key)}
                          disabled={savingKey === s.key}
                          className="btn-primary inline-flex items-center gap-1 text-[10px]">
                          {savingKey === s.key
                            ? <RefreshCw size={10} className="animate-spin" />
                            : <Save size={10} />}
                          Save
                        </button>
                      ) : (
                        <CheckCircle2 size={12} className="text-emerald/50 inline-block" />
                      )}
                    </td>
                  </tr>
                )
              })}
              {settings.length === 0 && !loading && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-text-muted italic">
                  Settings seed on first access — refresh.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Editable rules */}
      <section className="card p-0">
        <header className="p-3 border-b border-border flex items-center gap-2">
          <Clock size={14} className="text-blue-light" />
          <h2 className="text-sm font-semibold uppercase tracking-widest">Rules · edit minutes saved per event</h2>
          <span className="text-[10px] text-text-muted ml-1">saves invalidate the summary cache immediately</span>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised text-text-muted">
              <tr>
                <th className="text-left  px-3 py-2 w-8"></th>
                <th className="text-left  px-3 py-2">Category</th>
                <th className="text-left  px-3 py-2 w-1/4">Label</th>
                <th className="text-left  px-3 py-2 w-2/5">Description</th>
                <th className="text-right px-3 py-2">Minutes each</th>
                <th className="text-right px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => {
                const d = dirty[r.category] || {}
                const isDirty = Object.keys(d).length > 0
                const merged = { ...r, ...d }
                return (
                  <tr key={r.category} className={clsx('border-t border-border', !merged.enabled && 'opacity-50')}>
                    <td className="px-3 py-2">
                      <input type="checkbox"
                        checked={!!merged.enabled}
                        onChange={e => patch(r.category, 'enabled', e.target.checked)}
                        aria-label={`Enable ${r.category}`} />
                    </td>
                    <td className="px-3 py-2 font-mono text-text-muted">{r.category}</td>
                    <td className="px-3 py-2">
                      <input className="input w-full" value={merged.label}
                        aria-label={`Label for ${r.category}`}
                        onChange={e => patch(r.category, 'label', e.target.value)} />
                    </td>
                    <td className="px-3 py-2">
                      <input className="input w-full" value={merged.description}
                        aria-label={`Description for ${r.category}`}
                        onChange={e => patch(r.category, 'description', e.target.value)} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input className="input text-right w-20 font-mono" type="number" step="0.1"
                        aria-label={`Minutes saved per ${r.category} event`}
                        value={merged.minutes_each}
                        onChange={e => patch(r.category, 'minutes_each', parseFloat(e.target.value))} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isDirty ? (
                        <button type="button" onClick={() => saveRule(r.category)}
                          disabled={savingRule === r.category}
                          className="btn-primary inline-flex items-center gap-1 text-[10px]">
                          {savingRule === r.category
                            ? <RefreshCw size={10} className="animate-spin" />
                            : <Save size={10} />}
                          Save
                        </button>
                      ) : (
                        <CheckCircle2 size={12} className="text-emerald/50 inline-block" />
                      )}
                    </td>
                  </tr>
                )
              })}
              {rules.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-text-muted italic">
                  No rules yet. Hitting this page for the first time will seed defaults.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function KpiTile({ label, hours, trend, tone, windowDays }: {
  label: string; hours: number;
  trend?: { date: string; hours: number; gbp: number }[];
  tone: 'rose' | 'emerald' | 'amber' | 'blue';
  windowDays: number;
}) {
  const c = tone === 'rose' ? 'text-rose' : tone === 'emerald' ? 'text-emerald' : tone === 'amber' ? 'text-amber-light' : 'text-blue-light'
  const stroke = tone === 'rose' ? '#f43f5e' : tone === 'emerald' ? '#10b981' : tone === 'amber' ? '#f59e0b' : '#3b82f6'
  const slice = trend ? trend.slice(-windowDays) : []
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className="flex items-end justify-between mt-1 gap-2">
        <div className={clsx('text-2xl font-black font-mono', c)}>{fmtHours(hours)}</div>
        {slice.length >= 2 && <MiniSparkline points={slice.map(p => p.hours)} stroke={stroke} />}
      </div>
      <div className="text-[10px] text-text-muted font-mono mt-0.5">analyst hours avoided</div>
    </div>
  )
}

/** Bare-svg sparkline — shared by KPI tiles and the agent leaderboard
 *  trend column. ~20px tall so it never disrupts row heights. */
function MiniSparkline({ points, stroke, width = 80, height = 22 }: {
  points: number[]; stroke: string; width?: number; height?: number;
}) {
  if (points.length < 2) return null
  const max = Math.max(1, ...points)
  const min = Math.min(0, ...points)
  const range = max - min || 1
  const x = (i: number) => (i / (points.length - 1)) * (width - 2) + 1
  const y = (v: number) => height - 2 - ((v - min) / range) * (height - 4)
  const d = points.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  return (
    <svg width={width} height={height} className="shrink-0">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(points.length - 1)} cy={y(points[points.length - 1])} r="1.8" fill={stroke} />
    </svg>
  )
}

function DailyTrendChart({ data }: { data: { date: string; hours: number; gbp: number }[] }) {
  const W = 900, H = 180, PAD_L = 48, PAD_R = 16, PAD_T = 14, PAD_B = 26
  if (data.length < 2) return <div className="text-xs text-text-muted italic py-4">Not enough data yet.</div>
  const max = Math.max(1, ...data.map(d => d.gbp))
  const xFor = (i: number) => PAD_L + (i / (data.length - 1)) * (W - PAD_L - PAD_R)
  const yFor = (v: number) => PAD_T + (1 - v / max) * (H - PAD_T - PAD_B)
  const path = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(d.gbp)}`).join(' ')
  const area = path + ` L${xFor(data.length - 1)},${H - PAD_B} L${xFor(0)},${H - PAD_B} Z`
  const ticks = [0, 1, 2, 3, 4].map(i => (max * i) / 4)
  return (
    <div className="overflow-x-auto">
      <svg width={W} height={H} className="max-w-full">
        <defs>
          <linearGradient id="sav-trend" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#10b981" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
        </defs>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={PAD_L} x2={W - PAD_R} y1={yFor(t)} y2={yFor(t)}
              stroke="#27272a" strokeDasharray={i === 0 ? undefined : '2 4'} />
            <text x={PAD_L - 6} y={yFor(t) + 3} fontSize="9" fill="#94a3b8" textAnchor="end">
              £{Math.round(t).toLocaleString()}
            </text>
          </g>
        ))}
        <path d={area} fill="url(#sav-trend)" />
        <path d={path} fill="none" stroke="#10b981" strokeWidth="2" />
        {data.map((d, i) => i % 5 === 0 ? (
          <text key={d.date} x={xFor(i)} y={H - 8} fontSize="9" fill="#94a3b8" textAnchor="middle">
            {d.date.slice(5)}
          </text>
        ) : null)}
      </svg>
    </div>
  )
}
